"use client";

import React, { useMemo } from "react";
import { CountdownTimer } from "./CountdownTimer";
import { mergeHistoryWithClaimData, groupResultsByEpoch } from "./utils";
import { UsdtIcon, EthIcon, UsdcIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { REWARD_SYMBOL } from "@/constants";
import type { ClaimRewardsError } from "@/hooks";
import {
  useSpinningWheelHistory,
  useClaimSpinningWheelRewards,
  useSpinningWheelThresholdProgress,
} from "@/hooks/spinningWheel";
import { CLAIM_REWARDS_ERROR_TYPE } from "@/hooks/user/useClaimRewards";
import { calculateRewardTotals, formatDateUTC, getRewardIcon, getRewardTextColorClass } from "@/utils/wheel";
import Decimal from "decimal.js";
import { History } from "lucide-react";
import { useSnackbar } from "notistack";

/**
 * Displays the user's spinning wheel history with total wins and amounts
 */
export const WheelHistory: React.FC = () => {
  const { results, isLoading: historyLoading, refetch } = useSpinningWheelHistory();
  const { enqueueSnackbar } = useSnackbar();

  // Threshold progress for pending rewards
  const {
    currentUsdValue,
    thresholdUsdValue,
    isThresholdReached,
    progressPercentage,
    pendingResultsCount,
    isLoading: thresholdLoading,
  } = useSpinningWheelThresholdProgress();

  // Claim rewards integration
  const {
    rewardDetails,
    claimableSoonRewardDetails,
    claimablesData,
    claimReward,
    isLoading: claimLoading,
  } = useClaimSpinningWheelRewards({
    onClaimSuccess: () => {
      enqueueSnackbar("Reward claimed successfully!", { variant: "success" });
      refetch(); // Refresh history to update claim status
    },
    onClaimError: (error: ClaimRewardsError) => {
      if (error.type === CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED) return;
      enqueueSnackbar(error.message || "Failed to claim reward", { variant: "error" });
    },
  });

  // Merge history with claimable rewards data and group by epoch
  const epochGroups = useMemo(() => {
    if (!results || results.length === 0) return [];

    // Convert rewardDetails maps to arrays for merging
    const claimableArray = rewardDetails ? Object.values(rewardDetails).flat() : [];
    const claimableSoonArray = claimableSoonRewardDetails ? Object.values(claimableSoonRewardDetails).flat() : [];

    const merged = mergeHistoryWithClaimData(results, claimableArray, claimableSoonArray, claimablesData);

    // Group by epoch
    const grouped = groupResultsByEpoch(merged);

    return grouped;
  }, [results, rewardDetails, claimableSoonRewardDetails, claimablesData]);

  // Calculate total wins and amounts from all results across all epochs
  const { totalWins, totalAmount } = useMemo(() => {
    if (!epochGroups || epochGroups.length === 0) {
      return { totalWins: 0, totalAmount: { usdt: new Decimal(0), eth: new Decimal(0), usdc: new Decimal(0) } };
    }

    // Flatten all results from all epoch groups
    const allResults = epochGroups.flatMap((group) => group.results);

    return {
      totalWins: allResults.length,
      totalAmount: calculateRewardTotals(
        results?.map((r) => ({
          rewardSymbol: r.rewardSymbol,
          rewardAmount: new Decimal(r.rewardAmount),
        })) || [],
      ),
    };
  }, [epochGroups]);

  // Calculate total claimable amounts (only from claimable epochs)
  const totalClaimable = useMemo(() => {
    if (!epochGroups || epochGroups.length === 0) {
      return { usdt: new Decimal(0), eth: new Decimal(0), usdc: new Decimal(0) };
    }

    const claimableGroups = epochGroups.filter((group) => group.status === "claimable");

    return calculateRewardTotals(
      claimableGroups.flatMap((group) =>
        group.results.map((r) => ({
          rewardSymbol: r.rewardSymbol,
          rewardAmount: new Decimal(r.rewardAmount),
        })),
      ),
    );
  }, [epochGroups]);

  const hasClaimableRewards =
    totalClaimable.usdt.greaterThan(0) || totalClaimable.eth.greaterThan(0) || totalClaimable.usdc.greaterThan(0);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Total Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="text-xs text-cede-text-secondary mb-1">Spins Amount</div>
          <div className="text-2xl font-bold text-cede-primary">{totalWins}</div>
        </div>
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="text-xs text-cede-text-secondary mb-2">Total Won</div>
          {totalAmount.usdt.equals(0) && totalAmount.eth.equals(0) && totalAmount.usdc.equals(0) ? (
            <div className="text-lg font-semibold text-cede-text-secondary">-</div>
          ) : (
            <div className="flex flex-col gap-1">
              {totalAmount.usdt.greaterThan(0) && (
                <div className="flex items-center gap-1.5 text-sm">
                  <UsdtIcon width={16} height={16} />
                  <span className="font-semibold">
                    {totalAmount.usdt.toNumber()} {REWARD_SYMBOL.USDT}
                  </span>
                </div>
              )}
              {totalAmount.eth.greaterThan(0) && (
                <div className="flex items-center gap-1.5 text-sm">
                  <EthIcon width={16} height={16} />
                  <span className="font-semibold">
                    {totalAmount.eth.toNumber()} {REWARD_SYMBOL.WETH}
                  </span>
                </div>
              )}
              {totalAmount.usdc.greaterThan(0) && (
                <div className="flex items-center gap-1.5 text-sm">
                  <UsdcIcon width={16} height={16} />
                  <span className="font-semibold">
                    {totalAmount.usdc.toNumber()} {REWARD_SYMBOL.USDC}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pending Rewards Section - Show when user has pending rewards below threshold */}
      {pendingResultsCount > 0 && !isThresholdReached && !thresholdLoading && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-xs text-cede-text-secondary mb-1">Pending Rewards</div>
                <div className="text-sm font-semibold text-cede-primary">
                  {pendingResultsCount} {pendingResultsCount === 1 ? "reward" : "rewards"} waiting
                </div>
              </div>
              <Badge variant="secondary" className="text-xs h-6 flex items-center">
                Accumulating
              </Badge>
            </div>

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-cede-text-secondary">
                  ${currentUsdValue.toFixed(2)} / ${thresholdUsdValue.toFixed(2)} USD
                </span>
                <span className="text-cede-primary font-semibold">{progressPercentage.toFixed(0)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>

            <div className="text-xs text-cede-text-secondary">
              Keep spinning! Rewards become claimable when you reach ${thresholdUsdValue.toFixed(2)} USD in total.
            </div>
          </div>
        </div>
      )}

      {/* Claimable Rewards Section - Full Width */}
      {hasClaimableRewards && (
        <div className="bg-cede-bg-component rounded-large border-container p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-xs text-cede-text-secondary mb-2">Ready to Claim</div>
              <div className="flex items-center gap-3 flex-wrap">
                {totalClaimable.usdt.greaterThan(0) && (
                  <div className="flex items-center gap-1.5">
                    <UsdtIcon width={16} height={16} />
                    <span className="font-semibold text-sm">
                      {totalClaimable.usdt.toNumber()} {REWARD_SYMBOL.USDT}
                    </span>
                  </div>
                )}
                {totalClaimable.eth.greaterThan(0) && (
                  <div className="flex items-center gap-1.5">
                    <EthIcon width={16} height={16} />
                    <span className="font-semibold text-sm">
                      {totalClaimable.eth.toNumber()} {REWARD_SYMBOL.WETH}
                    </span>
                  </div>
                )}
                {totalClaimable.usdc.greaterThan(0) && (
                  <div className="flex items-center gap-1.5">
                    <UsdcIcon width={16} height={16} />
                    <span className="font-semibold text-sm">
                      {totalClaimable.usdc.toNumber()} {REWARD_SYMBOL.USDC}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button onClick={() => claimReward(undefined)} disabled={claimLoading} size="medium" className="px-8 ml-4">
              {claimLoading ? "Loading..." : "Claim All"}
            </Button>
          </div>
        </div>
      )}

      {/* History List */}
      {historyLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-cede-text-secondary">Loading...</div>
        </div>
      ) : epochGroups && epochGroups.length > 0 ? (
        <div className="space-y-3">
          {epochGroups.map((epochGroup) => {
            // Determine if there are multiple results in this epoch
            const hasMultipleResults = epochGroup.results.length > 1;

            return (
              <div key={epochGroup.epochId} className="space-y-2">
                {/* Epoch container with all rewards */}
                <div className="bg-cede-bg-component rounded-large border-container p-3">
                  {/* Epoch header showing total rewards and status */}
                  <div className="flex items-center justify-between mb-2">
                    {/* Left: Total rewards for this epoch */}
                    <div className="flex flex-col gap-1">
                      <div className="text-xs text-cede-text-secondary">
                        Epoch {epochGroup.epochId} ({epochGroup.results.length}{" "}
                        {epochGroup.results.length === 1 ? "spin" : "spins"})
                      </div>
                      <div className="flex items-center gap-3">
                        {epochGroup.totalRewards.USDT && (
                          <div className="flex items-center gap-1.5">
                            <UsdtIcon width={16} height={16} />
                            <span className="font-semibold text-cede-usdt-secondary text-sm">
                              {epochGroup.totalRewards.USDT} USDT
                            </span>
                          </div>
                        )}
                        {epochGroup.totalRewards.WETH && (
                          <div className="flex items-center gap-1.5">
                            <EthIcon width={16} height={16} />
                            <span className="font-semibold text-cede-eth-primary text-sm">
                              {epochGroup.totalRewards.WETH} ETH
                            </span>
                          </div>
                        )}
                        {epochGroup.totalRewards.USDC && (
                          <div className="flex items-center gap-1.5">
                            <UsdcIcon width={16} height={16} />
                            <span className="font-semibold text-cede-usdc-primary text-sm">
                              {epochGroup.totalRewards.USDC} USDC
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Status badge */}
                    <div className="flex flex-col items-end justify-center gap-1 min-w-[120px]">
                      {epochGroup.status === "claimable" && (
                        <Badge
                          variant="primary"
                          className="text-xs h-6 flex items-center bg-cede-primary/20 text-cede-primary"
                        >
                          Claimable
                        </Badge>
                      )}

                      {epochGroup.status === "pending" && (
                        <>
                          <Badge variant="secondary" className="text-xs h-6 flex items-center">
                            Pending
                          </Badge>
                          {epochGroup.cooldownEnd && (
                            <div className="text-xs text-cede-text-secondary flex items-center gap-1 pt-4 h-4">
                              <span className="whitespace-nowrap">Claimable in</span>
                              <span className="tabular-nums">
                                <CountdownTimer endDate={epochGroup.cooldownEnd} isEstimated={false} />
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {epochGroup.status === "claimed" && (
                        <>
                          <Badge variant="success" className="text-xs h-6 flex items-center">
                            Claimed ✓
                          </Badge>
                          <div className="text-xs text-cede-text-secondary h-4 pt-4 flex items-center">
                            {epochGroup.results[0] && formatDateUTC(new Date(epochGroup.results[0].createdAt))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Individual spins within the epoch (only show if multiple) */}
                  {hasMultipleResults && (
                    <div className="mt-3 pt-3 border-t border-cede-border space-y-2">
                      {epochGroup.results.map((result) => {
                        return (
                          <div key={result.id} className="flex items-center gap-2 text-sm">
                            {getRewardIcon(result.rewardSymbol, { width: 16, height: 16 })}
                            <span className={getRewardTextColorClass(result.rewardSymbol)}>
                              {new Decimal(result.rewardAmount).toNumber()} {result.rewardSymbol}
                            </span>
                            <span className="text-cede-text-secondary text-xs ml-auto">
                              {formatDateUTC(new Date(result.createdAt))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center justify-center py-8">
          <div className="text-center text-cede-text-secondary">
            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No wins yet. Spin the wheel to get started!</p>
          </div>
        </div>
      )}
    </div>
  );
};
